// packages/web-ui/src/components/inline-cards/ConnectionsTableCard.tsx
//
// Deterministic "Connected to" table card. Renders the COMPLETE neighbour set
// of a queried entity, derived server-side by `buildEntityAdjacency` (the same
// projection the entity-detail panel uses) and delivered on the
// `kb:entityConnections` SSE event. This card is the authoritative, complete
// enumeration — the LLM's prose answer sits above it and no longer has to
// transcribe every connection row into markdown (which reproducibly dropped
// rows). Pure data card: no per-card handlers, mirroring `ChangesetPreviewCard`.

import type { JSX } from 'react';
import { useCallback, useState } from 'react';
import { flowColorForInterfaceType } from '@tessa-lean/base';
import { CardShell } from './CardShell.js';
import type { EntityConnectionsEnvelopeData, EntityConnectionsRow } from '../../state/inline-cards.js';
import { kbApi } from '../../api/clients/kb.js';
import { viewsApi } from '../../api/clients/views.js';
import { createApiContext, type ApiContext } from '../../api/client.js';
import { useRootStore } from '../../state/store.js';

export interface ConnectionsTableCardProps extends EntityConnectionsEnvelopeData {
  /** Test seam — stub api context. Defaults to the live `createApiContext()`. */
  apiContext?: ApiContext;
  /**
   * Test seam — open a view in the Views tab. Defaults to the root store's
   * `openTab({ destination: 'views', … })`.
   */
  onOpenView?: (viewId: string, label?: string) => void;
}

function openViewInStore(viewId: string, label?: string): void {
  useRootStore.getState().openTab({
    destination: 'views',
    resourceId: viewId,
    label: label ?? viewId,
    activate: true,
  });
}

type EditStatus = 'idle' | 'saving' | 'error';

/** Short simple-name of an interface/flow type for the chip label. */
function flowLabel(flowType: string): string {
  return flowType.split('::').pop() ?? flowType;
}

function DirectionArrow(props: { direction: 'incoming' | 'outgoing' }): JSX.Element {
  const incoming = props.direction === 'incoming';
  return (
    <span
      aria-label={incoming ? 'incoming' : 'outgoing'}
      title={incoming ? 'incoming' : 'outgoing'}
      className="font-mono text-taupe-700"
    >
      {incoming ? '←' : '→'}
    </span>
  );
}

function FlowChips(props: { flowTypes: string[] }): JSX.Element {
  if (props.flowTypes.length === 0) {
    return <span className="text-taupe-700">—</span>;
  }
  return (
    <span className="inline-flex flex-wrap gap-1">
      {props.flowTypes.map((ft) => {
        const color = flowColorForInterfaceType(ft);
        return (
          <span
            key={ft}
            data-testid="connections-flow-chip"
            className="inline-flex items-center rounded-none border px-1 text-[10px] font-medium"
            style={{ borderColor: color, color }}
          >
            {flowLabel(ft)}
          </span>
        );
      })}
    </span>
  );
}

function PortsCell(props: { ports: EntityConnectionsRow['ports'] }): JSX.Element {
  const pairs = props.ports.filter((p) => p.targetPort.length > 0 || p.neighbourPort.length > 0);
  if (pairs.length === 0) {
    return <span className="text-taupe-700">—</span>;
  }
  return (
    <span className="inline-flex flex-col gap-0.5 font-mono text-[11px]">
      {pairs.map((p, i) => (
        <span key={`${p.targetPort}-${p.neighbourPort}-${i}`}>
          {p.targetPort.length > 0 ? p.targetPort : '·'}
          <span className="text-taupe-700"> → </span>
          {p.neighbourPort.length > 0 ? p.neighbourPort : '·'}
        </span>
      ))}
    </span>
  );
}

export function ConnectionsTableCard(props: ConnectionsTableCardProps): JSX.Element {
  const rows = props.rows;
  const neighbourCount = rows.length;
  const openView = props.onOpenView ?? openViewInStore;
  const [editStatus, setEditStatus] = useState<EditStatus>('idle');
  const [editError, setEditError] = useState<string | null>(null);

  // "Edit" persists a COPY of the deterministic CONTEXT diagram of this entity
  // as a new view and opens it in the Views-tab editor (where the diagram-agent
  // handles change requests). We fetch the KB-grounded context layout fresh (the
  // same `buildEntityContextLayout` the diagram + table both derive from), so
  // the copy is authoritative — not a re-serialisation of the rendered card.
  const handleEdit = useCallback(() => {
    setEditStatus('saving');
    setEditError(null);
    const ctx = props.apiContext ?? createApiContext();
    void (async () => {
      const layoutRes = await kbApi.getEntityContext(ctx, props.targetQualifiedName);
      if (!layoutRes.ok) {
        setEditStatus('error');
        setEditError(layoutRes.error.message);
        return;
      }
      const createRes = await viewsApi.createView(ctx, layoutRes.value, props.targetQualifiedName);
      if (!createRes.ok) {
        setEditStatus('error');
        setEditError(createRes.error.message);
        return;
      }
      setEditStatus('idle');
      openView(createRes.value.viewId, layoutRes.value.title);
    })();
  }, [props.apiContext, props.targetQualifiedName, openView]);

  return (
    <CardShell
      role="region"
      ariaLabel={`Connections of ${props.target}`}
      cardKind="entity-connections"
      testId="connections-table-card"
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-2">
          <div className="font-medium text-fg" data-testid="connections-table-title">
            Connected to · <span className="font-mono">{props.target}</span>
          </div>
          <div className="flex items-center gap-2">
            <span
              data-testid="connections-table-count"
              className="text-[10px] font-medium uppercase tracking-wide text-taupe-700"
              aria-label={`${neighbourCount} neighbours, ${props.edgeCount} connections`}
            >
              {neighbourCount} neighbours · {props.edgeCount} connections
            </span>
            <button
              type="button"
              data-testid="connections-table-edit"
              aria-label="Edit a copy of this diagram in the Views tab"
              onClick={handleEdit}
              disabled={editStatus === 'saving'}
              className="rounded-none border border-taupe-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-fg hover:border-taupe-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {editStatus === 'saving' ? 'Opening…' : 'Edit'}
            </button>
          </div>
        </div>
        {editStatus === 'error' && editError !== null ? (
          <div
            role="status"
            data-testid="connections-table-edit-error"
            className="text-[11px] text-accent-red"
          >
            Couldn’t open editor: {editError}
          </div>
        ) : null}
        <div className="overflow-x-auto">
          <table
            data-testid="connections-table"
            className="w-full border-collapse text-xs"
          >
            <thead>
              <tr className="border-b border-taupe-300 text-left text-taupe-700">
                <th className="py-1 pr-3 font-medium">Neighbour</th>
                <th className="py-1 pr-3 font-medium">Dir</th>
                <th className="py-1 pr-3 font-medium">Flow type(s)</th>
                <th className="py-1 font-medium">Port(s)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={`${r.direction}:${r.entityId}`}
                  data-testid="connections-table-row"
                  className="border-b border-taupe-200 align-top"
                >
                  <td className="py-1 pr-3 font-mono">
                    {r.neighbour}
                    {r.level !== undefined ? (
                      <span className="ml-1 text-[10px] text-taupe-700">({r.level})</span>
                    ) : null}
                  </td>
                  <td className="py-1 pr-3">
                    <DirectionArrow direction={r.direction} />
                  </td>
                  <td className="py-1 pr-3">
                    <FlowChips flowTypes={r.flowTypes} />
                  </td>
                  <td className="py-1">
                    <PortsCell ports={r.ports} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </CardShell>
  );
}
